import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import apis from '@/apis';
import { storageUtil } from '@/utils';

import type { AppDispatch, RootState } from './store';

const { common: commonApi } = apis;
const STORAGE_KEY = 'globalSettings';

interface IGlobalSettingsAccount {
  addCharmLvLimit?: number;
  addWealthLvLimit?: number;
  duidBg?: string;
  duidIcon?: string;
  female?: string;
  guildIcon?: string;
  levelIcon?: string;
  levelLabel?: string;
  male?: string;
  unknown?: string;
  [key: string]: unknown;
}

export interface IGlobalSettings {
  account?: IGlobalSettingsAccount;
  common?: {
    addCharmLvLimit?: number;
    addWealthLvLimit?: number;
    allowAddCharm?: boolean;
    allowAddWealth?: boolean;
    diamondExchangeGoldRate?: number;
    moduleState?: number;
    whatsapp?: string;
    [key: string]: unknown;
  };
  gift?: Record<string, unknown>;
  h5?: Record<string, string>;
  im?: Record<string, unknown>;
  room?: Record<string, unknown>;
  rtc?: Record<string, unknown>;
  vip?: Record<string, unknown>;
}

interface SettingsResponse {
  code: number;
  data: IGlobalSettings;
  msg?: string;
}

// 读取当前会话缓存；缓存格式异常时清除，避免初始化 store 失败
function getStoredSettings(): IGlobalSettings | null {
  try {
    return storageUtil.getSessionStorage(STORAGE_KEY, true) || null;
  } catch {
    storageUtil.removeSessionStorage(STORAGE_KEY);
    return null;
  }
}

// 复用尚未完成的请求，避免多个组件同时挂载时重复调用接口
let pendingRequest: Promise<IGlobalSettings> | null = null;

function fetchSettings() {
  if (!pendingRequest) {
    pendingRequest = commonApi
      .getSettings()
      .then((response) => {
        const { data } = response as SettingsResponse;
        // 接口成功后写入会话缓存，刷新页面后可以直接恢复配置
        storageUtil.setSessionStorage(STORAGE_KEY, data);
        return data;
      })
      .finally(() => {
        // 请求结束后释放 Promise，失败时允许后续组件重新发起请求
        pendingRequest = null;
      });
  }

  return pendingRequest;
}

export const slice = createSlice({
  name: 'globalSettings',
  // 优先使用当前会话缓存，没有缓存时由 hook 自动请求
  initialState: getStoredSettings() as IGlobalSettings | null,
  reducers: {
    // 全局配置由接口完整返回，因此直接替换旧配置
    setGlobalSettings: (
      _state,
      action: PayloadAction<IGlobalSettings>,
    ) => action.payload,
  },
});

export const { setGlobalSettings } = slice.actions;
export const reducer = slice.reducer;

export default function useGlobalSettingsStore() {
  const dispatch = useDispatch<AppDispatch>();
  const settings = useSelector(
    (state: RootState) => state.globalSettings,
  );

  // 首次使用且没有缓存配置时自动加载；请求结果写入 Redux 供所有组件共享
  useEffect(() => {
    if (settings) return;

    fetchSettings()
      .then((data) => {
        dispatch(setGlobalSettings(data));
      })
      .catch((requestError: unknown) => {
        console.error('Failed to load global settings', requestError);
      });
  }, [dispatch, settings]);

  // 根据服务端下发的模板生成指定分区和等级的公会头像边框地址
  const getGuildIconUrl = useCallback(
    (region?: number, level?: number) => {
      const template = settings?.account?.guildIcon;
      if (!template || region == null || level == null) return '';

      return template
        .replace('{region}', String(region))
        .replace('{level}', String(level));
    },
    [settings],
  );

  return {
    settings,
    getGuildIconUrl,
  };
}
